import React, { FC, useEffect, useRef, useState } from 'react';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';

import { StyledGroupTrigger } from './Sidebar.styles';
import { NavItem, SidebarNavGroupProps } from './Sidebar.types';
import SidebarNavItem, { getNavItemKey } from './SidebarNavItem';

const itemKey = (item: NavItem): string => (item.path ? getNavItemKey(item) : item.action ?? item.text);

const SidebarNavGroup: FC<SidebarNavGroupProps> = ({ group, isCollapsed, activeItemKey, onNavigate, onClose }) => {
  const isItemActive = (item: NavItem): boolean => Boolean(item.path) && getNavItemKey(item) === activeItemKey;
  const hasActiveItem = activeItemKey !== null;
  const [isOpen, setIsOpen] = useState(group.defaultOpen || hasActiveItem);
  const hasBeenManuallyToggled = useRef(false);
  const prevHasActiveItem = useRef(hasActiveItem);

  useEffect(() => {
    const activeItemChanged = prevHasActiveItem.current !== hasActiveItem;

    if (hasActiveItem && !isOpen && (!hasBeenManuallyToggled.current || activeItemChanged)) {
      setIsOpen(true);
      hasBeenManuallyToggled.current = false;
    }

    prevHasActiveItem.current = hasActiveItem;
  }, [hasActiveItem, isOpen]);

  if (isCollapsed) {
    return (
      <Box sx={{ py: 1 }}>
        {group.items.map((item) => (
          <SidebarNavItem
            key={itemKey(item)}
            item={item}
            isActive={isItemActive(item)}
            isCollapsed={isCollapsed}
            onNavigate={onNavigate}
            onClose={onClose}
          />
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ py: 1 }}>
      <StyledGroupTrigger
        onClick={() => {
          hasBeenManuallyToggled.current = true;
          setIsOpen(!isOpen);
        }}
      >
        <Typography
          variant="caption"
          sx={{ fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}
        >
          {group.label}
        </Typography>
        {isOpen ? (
          <ExpandMoreIcon style={{ width: '16px', height: '16px' }} />
        ) : (
          <ChevronRightIcon style={{ width: '16px', height: '16px' }} />
        )}
      </StyledGroupTrigger>
      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        <List disablePadding sx={{ mt: 0.5 }}>
          {group.items.map((item) => (
            <SidebarNavItem
              key={itemKey(item)}
              item={item}
              isActive={isItemActive(item)}
              isCollapsed={isCollapsed}
              onNavigate={onNavigate}
              onClose={onClose}
            />
          ))}
        </List>
      </Collapse>
    </Box>
  );
};

export default React.memo(SidebarNavGroup);
